import React, { memo, useEffect, useMemo, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { DialogWrapper } from '@common/components/modal/dialog-wrapper';
import { Button } from '@common/components/button';
import { useAppSelector } from '@common/hooks';
import * as userSelector from '../../../core/selectors/logged-user';
import { EPermissions } from '@common/definitions';
import { Moment } from 'moment';
import * as organisationSelector from '../../../organisation/selectors/organisation';
import * as networkSelector from '../../../network/selectors/network';
import { useRequest } from '@common/services/api';
import { useCalendar } from '@modules/calendar/context/calendar-context';
import { ConfirmButton } from '@common/components/confirm-button';
import deleteSchedule from '@modules/calendar/actions/delete-schedule';
import { AlertService } from '@common/services/alert';
import Permission from '@common/components/permission';

type ScheduleModelProps = {
  currentWeek: Moment;
};

const ScheduleModel = memo(({ currentWeek }: ScheduleModelProps) => {
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const organisation = useAppSelector(organisationSelector.selected);
  const { refetchSchedules } = useCalendar();
  if (
    organisation?.integrations?.length &&
    organisation.integrations.find(
      (integration) => integration.external_schedule
    )
  ) {
    return null;
  }
  const network = useAppSelector((state) => networkSelector.selected(state));
  const functions = useAppSelector((state) => userSelector.functions(state, EPermissions.NETWORK_SCHEDULES_VIEW_ALL));
  const { t } = useTranslation();
  const week = currentWeek.isoWeek();
  const year = currentWeek.isoWeekYear();

  const { data: schedules, loading: loadingSchedules, refetch: refetchSchedulesRequest } = useRequest(
    `/v2/organisations/${organisation?.id}/calendar/schedules`,
    {
      payload: { week, year },
    }
  );
  useEffect(() => {
    if (organisation?.id) {
      refetchSchedulesRequest();
    }
  }, [week, year, organisation?.id, refetchSchedules]);

  const filteredSchedules = useMemo(() => {
    return schedules?.filter((schedule: any) => schedule.network_id === network?.id).map((schedule: any) => ({
      ...schedule,
      network,
      function: schedule.function_id
        ? functions?.find((f: any) => f.id === schedule.function_id)
        : null,
    }));
  }, [schedules, network, functions]);
  const handleHide = () => {
    setShowScheduleModal(false);
  };


  const handleDelete = async (scheduleId: number) => {
    try {
      await deleteSchedule(scheduleId);
      refetchSchedulesRequest();
      AlertService.success(t('schedules:schedule_deleted'));
    } catch (error: any) {
      AlertService.error(t('schedules:schedule_delete_error'));
    }
  };
  return (
    <>
      {filteredSchedules.length > 0 && (
        <Button type="white" size="default" isLoading={loadingSchedules} onClick={() => setShowScheduleModal(true)}>
          <Trans i18nKey="calendar:view_week_schedules" />
        </Button>
      )}
      <DialogWrapper
        show={showScheduleModal}
        onHide={handleHide}
        title={t('calendar:week_schedules')}
        hideFooter
        content={
          <div className="BirthdaysList ">
            {filteredSchedules?.map((schedule: any) => (
              <div
                className={`tw-flex tw-items-center tw-justify-between tw-border-0 last:tw-border-b-0 tw-border-b 
                tw-border-solid tw-border-gray-200 tw-mx-[20px] tw-py-[12px]`}
                key={schedule.id}
              >
                <span>{schedule.function?.name ?? schedule.network?.name}</span>
                <a href={schedule.path} target="_blank" rel="noreferrer" className="tw-mr-2 tw-ml-auto">
                  <Button type="primary">
                    <Trans i18nKey="schedules:view_button" />
                  </Button>
                </a>
                <Permission name={[
          EPermissions.NETWORK_FUNCTION_MEMBERSHIPS_SCHEDULES_REMOVE,
          EPermissions.NETWORK_SCHEDULES_REMOVE,
        ]}
                >
                  <ConfirmButton
                    danger
                    title={t('schedules:delete_button')}
                    description={t('schedules:deletion_description')}
                    onConfirm={() => handleDelete(Number(schedule.id))}
                    confirmText={t('schedules:delete_confirmation_button')}
                  >
                    <Button icon="delete" />
                  </ConfirmButton>
                </Permission>
              </div>
            ))}
          </div>
        }
      />
    </>
  );
});

export default ScheduleModel;
